import { Component, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, take, throttleTime } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import { profileEdit, profileReload, profileSelectIcons, profileSelectTheme } from '../../../store/profile/profile.actions';
import {
    selectConsumerTheme,
    selectProfileBusy,
    selectProfileIdentityName,
    selectProfileReady, selectRole
} from '../../../store/profile/profile.state';
import { logout } from 'src/app/store/login/login.actions';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { TitleService } from '../../shared/services/title.service';
import { ActivatedRoute, ActivationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderElementsEnum } from '../../../enum/header-elements.enum';
import { selectCreateEntity } from '../../../store/interface/interface.state';
import { EntityEnum } from '../../../enum/entity.enum';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton, MatMiniFabButton } from '@angular/material/button';
import { FabExpandComponent } from '../fab-expand/fab-expand.component';
import { TopToolbarComponent } from '../top-toolbar/top-toolbar.component';
import { JournalComponent } from '../journal/journal.component';
import { MatTabGroup, MatTab } from '@angular/material/tabs';
import { SideMenuComponent } from '../side-menu/side-menu.component';
import { MatNavList } from '@angular/material/list';
import { ProfileLogoComponent } from '../profile-logo/profile-logo.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { ShipmentService } from '../../../data/shipment.service';
import { selectShipmentEditorShipment } from '../../../store/shipment-editor/shipment-editor.state';
import { GlobalSearchComponent } from '../../shared/global-search/global-search.component';
import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { RoleEnum } from '../../../enum/role.enum';
import { contactEditorLoadRequest, contactEditorNew } from '../../../store/contact-editor/contact-editor.actions';
import { ContactEditComponent } from '../../address-books/contact-edit/contact-edit.component';
import { MatDialog } from '@angular/material/dialog';
import { commodityEditorNew } from '../../../store/commodity-editor/commodity-editor.actions';
import { packageEditorNew } from '../../../store/package-editor/package-editor.actions';

@Component({
    selector: 'app-layout',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    imports: [MatSidenavContainer, MatSidenav, NgClass, ProfileLogoComponent, MatNavList, SideMenuComponent, MatTabGroup, MatTab, JournalComponent, MatSidenavContent, TopToolbarComponent, RouterOutlet, FabExpandComponent, MatMiniFabButton, MatTooltip, IconComponent, AsyncPipe, GlobalSearchComponent, LanguageSelectorComponent, MatButton]
})
export class LayoutComponent {

    @ViewChild('drawer')
    drawer: MatSidenav;
    title$: Observable<string>;
    public header: HeaderElementsEnum;
    public creating = false;

    constructor(private titleService: TitleService, private breakpointObserver: BreakpointObserver, private store: Store<AppState>,
                private router: Router, private activatedRoute: ActivatedRoute, private shipmentService: ShipmentService,
                private dialog: MatDialog) {
        this.title$ = this.titleService.title$;
    }

    isHandset$: Observable<boolean> = this.breakpointObserver.observe(Breakpoints.Handset)
        .pipe(
            map(result => result.matches),
            shareReplay()
        );

    profileBusy$: Observable<boolean> = this.store.select(selectProfileBusy);
    profileReady$: Observable<boolean> = this.store.select(selectProfileReady);
    createShipmentButton$: Observable<boolean> = this.store.select(selectCreateEntity).pipe(
        map((entity) => entity === EntityEnum.SHIPMENT)
    );
    createContactButton$: Observable<boolean> = this.store.select(selectCreateEntity).pipe(
        map((entity) => entity === EntityEnum.CONTACT)
    );

    profileIdentityName$: Observable<string | null> = this.store.select(selectProfileIdentityName);

    darkMode$: Observable<boolean> = this.store.select(selectConsumerTheme).pipe(
        map((name) => name.startsWith('theme-dark'))
    );

    canCreateContact$: Observable<boolean> = this.store.select(selectRole(RoleEnum.EDIT_CONTACTS));
    canCreateLibraryTemplate$: Observable<boolean> = this.store.select(selectRole(RoleEnum.PACKAGE_LIBRARY));

    wideMode$: Observable<boolean> = this.breakpointObserver.observe([ Breakpoints.Large, Breakpoints.Handset ])
        .pipe(
            map((result) => result.matches),
            shareReplay()
        );

    canDuplicateShipment$: Observable<boolean> = this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => !!shipment.id)
    );

    canReturnShipment$: Observable<boolean> = this.store.select(selectShipmentEditorShipment).pipe(
        map(shipment => !!shipment.id)
    );

    createShipment(): void {
        if (!this.creating) {
            this.creating = true;
            this.shipmentService.create().pipe(take(1)).subscribe({
                next: (shipment) => this.router.navigate([ 'shipment', shipment.id ]).then(),
                complete: () => this.creating = false
            });
        }
    }

    createContact(): void {
        this.store.dispatch(contactEditorNew());
    }

    createCommodityTemplate(): void {
        this.store.dispatch(commodityEditorNew());
    }

    createPackageTemplate(): void {
        this.store.dispatch(packageEditorNew());
    }

    duplicateShipment(): void {
        if (!this.creating) {
            this.creating = true;
            this.store.select(selectShipmentEditorShipment).pipe(
                switchMap(shipment => this.shipmentService.duplicate(shipment)),
                take(1)
            ).subscribe({
                next: (shipment) => this.router.navigate([ 'shipment', shipment.id ]).then(),
                complete: () => this.creating = false
            });
        }
    }

    returnShipment(): void {
        if (!this.creating) {
            this.creating = true;
            this.store.select(selectShipmentEditorShipment).pipe(
                switchMap(shipment => this.shipmentService.return(shipment)),
                take(1)
            ).subscribe({
                next: (shipment) => this.router.navigate([ 'shipment', shipment.id ]).then(),
                complete: () => this.creating = false
            });
        }
    }

    reloadProfile(): void {
        this.store.dispatch(profileReload());
    }

    logout(): void {
        this.store.dispatch(logout());
    }

    selectTheme(name: string): void {
        this.store.dispatch(profileSelectTheme({ name }));
    }

    darkModeChange(event: MatSlideToggleChange): void {
        if (event.checked) {
            this.store.dispatch(profileSelectTheme({ name: 'theme-dark-indigo-grey' }));
        } else {
            this.store.dispatch(profileSelectTheme({ name: 'theme-light-indigo-grey' }));
        }
    }

    setTheme(name: string): void {
        this.store.dispatch(profileSelectTheme({ name }));
    }

    setIcons(icons: string): void {
        this.store.dispatch(profileSelectIcons({ name: icons }));
    }

    sidenavClick(): void {
        if (this.drawer.mode === 'over') {
            this.drawer.close().then();
        }
    }

    headerElement(element: HeaderElementsEnum): Observable<boolean> {
        let child = this.activatedRoute;
        while (child.firstChild) {
            child = child.firstChild;
        }
        return this.router.events.pipe(
            startWith(new ActivationEnd(child.snapshot)),
            filter(event => event instanceof ActivationEnd),
            throttleTime(0), // only want first event
            map((event: ActivationEnd) => event.snapshot.data),
            map(data => data.header ? data.header : []),
            map(header => header.indexOf(element) !== -1)
        );
    }

    editProfile(): void {
        this.store.dispatch(profileEdit());
    }
}
